import React, { useState } from "react";
import { MapPin, Truck } from "lucide-react";

// ─── Public types (consumed by all POH pages) ───────────────────────────────

export type SigStatus = "confirmed" | "pending" | "upcoming";

export interface POHSigSlot {
  role: string;
  signerName?: string;
  signedAt?: string;
  signatureData?: string;
  geo?: { lat: number; lng: number };
  status: SigStatus;
}

export interface POHStepData {
  phase: "pickup" | "delivery";
  /** Derived from slots: confirmed only when every slot is confirmed */
  status: SigStatus;
  slots: POHSigSlot[];
}

export interface POHOverrideNote {
  /** Short label, e.g. "Driver pickup signature not captured" (pre-translated). */
  label: string;
  /** Optional meta line, e.g. "by Jane · 24 Jun, 14:30" (pre-translated). */
  meta?: string;
}

interface Props {
  pickup: POHStepData;
  delivery: POHStepData;
  pohRef?: string;
  onDownloadPdf?: () => void;
  /** Section heading for the override notes block (pre-translated). */
  overrideTitle?: string;
  /** Status steps that were advanced manually, bypassing a signature/file. */
  overrideNotes?: POHOverrideNote[];
}

// ─── Step bg / badge / text ──────────────────────────────────────────────────

const STEP_BG: Record<SigStatus, string> = {
  confirmed: "bg-emerald-50 border-emerald-300",
  pending:   "bg-amber-50 border-amber-300",
  upcoming:  "bg-gray-50 border-gray-200",
};

const BADGE_CLS: Record<SigStatus, string> = {
  confirmed: "bg-emerald-100 text-emerald-800",
  pending:   "bg-amber-100 text-amber-800",
  upcoming:  "bg-gray-200 text-gray-500",
};

// ─── Sig circle icon ─────────────────────────────────────────────────────────

const SigIcon: React.FC<{ status: SigStatus }> = ({ status }) => {
  if (status === "confirmed")
    return (
      <span className="w-[22px] h-[22px] rounded-full bg-emerald-500 text-white flex items-center justify-center text-[11px] font-bold shrink-0 select-none">
        ✓
      </span>
    );
  if (status === "pending")
    return (
      <span className="w-[22px] h-[22px] rounded-full bg-white border-2 border-dashed border-amber-400 text-amber-500 flex items-center justify-center text-[11px] font-bold shrink-0 select-none">
        ?
      </span>
    );
  return (
    <span className="w-[22px] h-[22px] rounded-full bg-gray-100 border border-gray-300 text-gray-400 flex items-center justify-center text-[11px] shrink-0 select-none">
      –
    </span>
  );
};

// ─── One signer row ──────────────────────────────────────────────────────────

const SigRow: React.FC<{
  slot: POHSigSlot;
  expanded: boolean;
  onToggle: () => void;
}> = ({ slot, expanded, onToggle }) => (
  <div className="py-1.5 border-b border-black/5 last:border-0">
    <div className="flex items-center gap-2">
      <SigIcon status={slot.status} />

      <div className="flex-1 min-w-0 overflow-hidden leading-tight">
        <p className="text-[11.5px] font-semibold text-gray-800">{slot.role}</p>

        {slot.status === "confirmed" && slot.signerName ? (
          <>
            <p className="text-[10.5px] text-gray-500 truncate">
              {slot.signerName}
              {slot.signedAt && (
                <>
                  {" · "}
                  {new Date(slot.signedAt).toLocaleString(undefined, {
                    day: "numeric", month: "short",
                    hour: "2-digit", minute: "2-digit",
                  })}
                </>
              )}
            </p>
            {slot.geo && (
              <a
                href={`https://maps.google.com/?q=${slot.geo.lat},${slot.geo.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-0.5 text-[10px] text-blue-500 hover:underline truncate max-w-full"
              >
                <MapPin className="h-2.5 w-2.5" />
                {slot.geo.lat.toFixed(4)}, {slot.geo.lng.toFixed(4)}
              </a>
            )}
          </>
        ) : slot.status === "pending" ? (
          <p className="text-[10.5px] text-amber-600 italic">Awaiting signature…</p>
        ) : (
          <p className="text-[10.5px] text-gray-400 italic">Pending pickup completion</p>
        )}
      </div>

      {slot.signatureData && slot.status === "confirmed" && (
        <button
          onClick={onToggle}
          className="text-[10px] text-gray-400 hover:text-blue-500 shrink-0 underline"
        >
          {expanded ? "Hide" : "View"}
        </button>
      )}
    </div>

    {expanded && slot.signatureData && (
      <div className="mt-2 ml-7 border border-gray-200 rounded-lg bg-white overflow-hidden">
        <img
          src={slot.signatureData}
          alt={`${slot.role} signature`}
          className="max-h-20 w-full object-contain"
        />
      </div>
    )}
  </div>
);

// ─── Step block (left or right column) ───────────────────────────────────────

const StepBlock: React.FC<{
  step: POHStepData;
  expandMap: Record<string, boolean>;
  onToggle: (key: string) => void;
}> = ({ step, expandMap, onToggle }) => {
  const confirmedCount = step.slots.filter((s) => s.status === "confirmed").length;
  const fraction =
    step.status === "pending" && step.slots.length > 0
      ? `${confirmedCount}/${step.slots.length}`
      : null;

  const badgeLabel =
    step.status === "confirmed" ? "✓ Done" :
    step.status === "pending" ? (fraction ? `⏳ ${fraction}` : "⏳ Pending") :
    "Upcoming";

  const phaseColor = step.phase === "pickup" ? "text-emerald-600" : "text-red-500";
  const phaseTitle = step.phase === "pickup" ? "POH · Pickup" : "POD · Delivery";

  return (
    <div
      className={`border rounded-xl p-3 h-full flex flex-col ${STEP_BG[step.status]} ${
        step.status === "upcoming" ? "opacity-60" : ""
      }`}
    >
      {/* Step header */}
      <div className="flex items-center justify-between mb-2.5">
        <span className={`text-[11.5px] font-bold uppercase tracking-wide ${phaseColor} flex items-center gap-1`}>
          {step.phase === "pickup" ? "◎" : "●"} {phaseTitle}
        </span>
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${BADGE_CLS[step.status]}`}>
          {badgeLabel}
        </span>
      </div>

      {/* Rows */}
      {step.status === "upcoming" ? (
        <p className="text-[11px] text-gray-400 italic text-center flex-1 flex items-center justify-center py-3">
          Activates after pickup is complete
        </p>
      ) : (
        step.slots.map((slot, i) => {
          const key = `${step.phase}-${i}`;
          return (
            <SigRow
              key={key}
              slot={slot}
              expanded={!!expandMap[key]}
              onToggle={() => onToggle(key)}
            />
          );
        })
      )}
    </div>
  );
};

// ─── Connector — horizontal (desktop) ────────────────────────────────────────

const ConnectorH: React.FC<{ pickupStatus: SigStatus; deliveryStatus: SigStatus }> = ({
  pickupStatus, deliveryStatus,
}) => {
  if (pickupStatus === "confirmed" && deliveryStatus === "confirmed") {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-1 py-4">
        <div className="h-0.5 w-full bg-emerald-400" />
        <span className="text-emerald-500 text-sm font-bold">✔✔</span>
        <div className="h-0.5 w-full bg-emerald-400" />
      </div>
    );
  }
  if (pickupStatus === "confirmed") {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-1 py-4">
        <div className="h-0.5 w-full" style={{ background: "linear-gradient(90deg,#10b981,#d1d5db)" }} />
        <Truck className="h-5 w-5 text-gray-400" />
        <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider text-center">In Transit</p>
        <div className="h-0.5 w-full" style={{ background: "linear-gradient(90deg,#d1d5db,#ef4444)" }} />
      </div>
    );
  }
  return (
    <div className="flex items-center justify-center h-full py-4">
      <span className="text-gray-400 text-xl tracking-widest select-none">···</span>
    </div>
  );
};

// ─── Connector — vertical (mobile) ───────────────────────────────────────────

const ConnectorV: React.FC<{ pickupStatus: SigStatus; deliveryStatus: SigStatus }> = ({
  pickupStatus, deliveryStatus,
}) => {
  const done = pickupStatus === "confirmed" && deliveryStatus === "confirmed";
  const inTransit = pickupStatus === "confirmed" && !done;

  return (
    <div className="flex items-center justify-center gap-3 py-1">
      <div className="flex-1 h-0.5" style={{ background: "linear-gradient(90deg,#10b981,#d1d5db)" }} />
      {done ? (
        <span className="text-emerald-500 text-sm font-bold shrink-0">✔✔</span>
      ) : inTransit ? (
        <div className="flex items-center gap-1 shrink-0">
          <Truck className="h-4 w-4 text-gray-400" />
          <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">In Transit</span>
        </div>
      ) : (
        <span className="text-gray-400 text-base tracking-widest shrink-0">···</span>
      )}
      <div className="flex-1 h-0.5" style={{ background: "linear-gradient(90deg,#d1d5db,#ef4444)" }} />
    </div>
  );
};

// ─── Main card ────────────────────────────────────────────────────────────────

const POHChainCard: React.FC<Props> = ({
  pickup,
  delivery,
  pohRef,
  onDownloadPdf,
  overrideTitle,
  overrideNotes,
}) => {
  const [expandMap, setExpandMap] = useState<Record<string, boolean>>({});

  const toggle = (key: string) =>
    setExpandMap((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <span className="text-sm">📋</span>
          <span className="text-sm font-semibold text-gray-900">Handover Confirmations</span>
        </div>
        <span className="text-[11px] text-gray-400 hidden md:block">
          Chain of custody · POH (loading) → POD (delivery)
        </span>
      </div>

      {/* Chain — vertical on mobile/tablet, horizontal grid on md+ */}
      <div className="p-4">
        {/* < md: stack vertically */}
        <div className="flex flex-col gap-2 md:hidden">
          <StepBlock step={pickup} expandMap={expandMap} onToggle={toggle} />
          <ConnectorV pickupStatus={pickup.status} deliveryStatus={delivery.status} />
          <StepBlock step={delivery} expandMap={expandMap} onToggle={toggle} />
        </div>
        {/* md+: side-by-side — min-w-0 prevents content from blowing out grid columns */}
        <div
          className="hidden md:grid items-stretch gap-3"
          style={{ gridTemplateColumns: "1fr 52px 1fr" }}
        >
          <div className="min-w-0"><StepBlock step={pickup} expandMap={expandMap} onToggle={toggle} /></div>
          <ConnectorH pickupStatus={pickup.status} deliveryStatus={delivery.status} />
          <div className="min-w-0"><StepBlock step={delivery} expandMap={expandMap} onToggle={toggle} /></div>
        </div>
      </div>

      {/* Manual status advancements — why a signature is missing for a step */}
      {overrideNotes && overrideNotes.length > 0 && (
        <div className="px-4 pb-3">
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-1.5">
            {overrideTitle && (
              <p className="text-[11px] font-bold uppercase tracking-wide text-amber-700">
                {overrideTitle}
              </p>
            )}
            <ul className="space-y-1">
              {overrideNotes.map((n, i) => (
                <li key={i} className="text-[11px] text-amber-900 leading-tight">
                  <span className="font-medium">{n.label}</span>
                  {n.meta && <span className="text-amber-700"> · {n.meta}</span>}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Footer */}
      {(pohRef || onDownloadPdf) && (
        <div className="flex justify-between items-center px-4 py-2.5 border-t border-dashed border-gray-200">
          {pohRef && (
            <span className="text-[11px] text-gray-500">{pohRef} · CMR audit trail active</span>
          )}
          {onDownloadPdf && (
            <button
              onClick={onDownloadPdf}
              className="text-[11px] text-blue-600 font-medium hover:underline"
            >
              ⬇ Download POH PDF
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default POHChainCard;
